import { MM_TO_PX, pageSizeMap } from "./page";

export type PageFormat = keyof typeof pageSizeMap;

/**
 * Defines how an individual item (e.g., experience entry) should be split
 * when it spans multiple pages.
 */
export interface SplitItemRange {
  itemId: string;
  startIndex?: number; // Starting content block index (0 = from beginning)
  endIndex?: number; // Ending content block index (undefined = to end)
  hideItemHeader?: boolean; // Hide item title/company for continuation pages
}

export interface LayoutItem {
  id: string; // The Section ID
  type: "section";
  itemIds?: string[]; // If defined, only render these items (by data-item-id)
  hideHeader?: boolean; // For continued sections on new pages
  height: number;
  top: number;
  // Content-level splitting: specifies which content blocks to render for split items
  splitItems?: SplitItemRange[];
}

export interface PageLayout {
  pageIndex: number;
  items: LayoutItem[];
  height: number;
}

/**
 * Layout information for a single column on a page (multi-column templates)
 */
export interface ColumnLayout {
  columnIndex: number;
  items: LayoutItem[];
  height: number;
}

/**
 * Page layout for multi-column templates (e.g., Arcanine with sidebar)
 */
export interface MultiColumnPageLayout {
  pageIndex: number;
  columns: ColumnLayout[];
  pageHeight: number; // Max of all column heights (determines page break)
}

// Minimum gap (in pixels) required before attempting content-level splitting.
// Small gaps (below this threshold) are acceptable - we only split when there's
// significant wasted space. ~80px ≈ 3-4 lines of text.
const MIN_GAP_FOR_CONTENT_SPLIT = 30;

// Bottom margin threshold to prevent content from going too close to page edge.
// This creates a small buffer at the bottom of each page for visual breathing room.
// ~16px provides a comfortable margin without wasting too much space.
const PAGE_BOTTOM_THRESHOLD_PX = 16;

// Safety margin for sub-pixel rendering differences (e.g. between Browser and Puppeteer)
// We SUBTRACT this to be conservative and prevent content from overflowing pages.
// Previously this was ADDED which caused visual overflow issues.
const LAYOUT_SAFETY_MARGIN_PX = 4;

export const calculateLayout = (
  container: HTMLElement,
  format: PageFormat = "a4",
  pageMargin: { top: number; bottom: number } = { top: 0, bottom: 0 },
): PageLayout[] => {
  const pageHeightPx = pageSizeMap[format].height * MM_TO_PX;
  // Apply bottom threshold to prevent content from going too close to page edge
  const contentHeightPx =
    pageHeightPx - (pageMargin.top + pageMargin.bottom) - PAGE_BOTTOM_THRESHOLD_PX;

  const pages: PageLayout[] = [];
  let currentPageItems: LayoutItem[] = [];
  let currentHeight = 0;
  let pageIndex = 0;

  const startNewPage = () => {
    pages.push({
      pageIndex,
      items: currentPageItems,
      height: currentHeight,
    });
    pageIndex++;
    currentPageItems = [];
    currentHeight = 0;
  };

  const children = Array.from(container.children) as HTMLElement[];

  // Measure container padding to ensure we don't overflow the physical page
  const containerStyle = window.getComputedStyle(container);
  const paddingTop = parseFloat(containerStyle.paddingTop) || 0;
  const paddingBottom = parseFloat(containerStyle.paddingBottom) || 0;

  // Reduce available content height by the container's vertical padding
  // effectiveHeight = A4 - (Margins + Padding) - SafetyMargin
  // We SUBTRACT a safety margin to prevent content from overflowing pages
  const effectiveContentHeightPx =
    contentHeightPx - (paddingTop + paddingBottom) - LAYOUT_SAFETY_MARGIN_PX;

  for (const child of children) {
    if (child.style.display === "none") continue;

    const id = child.getAttribute("data-id") || child.id;
    if (!id) continue;

    const childRect = child.getBoundingClientRect();
    const childHeight = childRect.height;
    const style = window.getComputedStyle(child);
    const marginTop = parseFloat(style.marginTop) || 0;

    // Effective margin logic: Ignore margin-top if it's the first item on the page
    const effectiveMarginTop = currentPageItems.length === 0 ? 0 : marginTop;
    const spaceNeeded = childHeight + effectiveMarginTop;

    // CASE 1: It fits completely
    if (currentHeight + spaceNeeded <= effectiveContentHeightPx) {
      currentPageItems.push({
        id,
        type: "section",
        height: childHeight,
        top: currentHeight + effectiveMarginTop,
      });
      currentHeight += spaceNeeded;
      continue;
    }

    // CASE 2: It doesn't fit, try to split
    const contentWrapper = child.querySelector('[data-role="section-content"]') as HTMLElement;

    // If no content wrapper, we can't split it. Move to next page.
    if (!contentWrapper) {
      if (currentHeight > 0) {
        startNewPage();
      }
      currentPageItems.push({
        id,
        type: "section",
        height: childHeight,
        top: currentHeight, // New page, top=0 (ignoring margin for first item)
      });
      currentHeight += childHeight;
      continue;
    }

    // We can try to split it.
    const contentWrapperRect = contentWrapper.getBoundingClientRect();
    const headerHeight = contentWrapperRect.top - childRect.top;
    const items = Array.from(contentWrapper.children) as HTMLElement[];

    // Check if header fits (with margin)
    if (currentHeight + effectiveMarginTop + headerHeight > effectiveContentHeightPx) {
      if (currentHeight > 0) {
        startNewPage();
      }
    }

    // Now we are calculating layout for this split section.
    // It might be on a new page (currentHeight=0) or continuing on current page.

    const currentMarginTop =
      currentPageItems.length === 0 ? 0 : parseFloat(getComputedStyle(child).marginTop) || 0;

    // Apply margin to currentHeight
    currentHeight += currentMarginTop;

    let spaceRemaining = effectiveContentHeightPx - currentHeight;
    let currentSectionHeightOnPage = 0;
    let placingHeader = true;

    // 1. Verify Header placement
    if (spaceRemaining >= headerHeight) {
      currentSectionHeightOnPage += headerHeight;
      spaceRemaining -= headerHeight;
    } else {
      // Should create new page if we haven't already
      startNewPage();
      // On new page
      currentHeight = 0;
      spaceRemaining = effectiveContentHeightPx;
      placingHeader = true;
      currentSectionHeightOnPage += headerHeight;
      spaceRemaining -= headerHeight;
    }

    let i = 0;
    const itemsOnCurrentPage: string[] = [];
    const splitItemsOnCurrentPage: SplitItemRange[] = [];

    // Helper function to measure content blocks within an item
    const measureContentBlocks = (itemElement: HTMLElement) => {
      const contentBlocks = Array.from(
        itemElement.querySelectorAll("[data-content-block]"),
      ) as HTMLElement[];

      if (contentBlocks.length === 0) return null;

      const itemRect = itemElement.getBoundingClientRect();

      // Get the item header height (content before first block)
      const firstBlock = contentBlocks[0];
      const firstBlockRect = firstBlock.getBoundingClientRect();
      const itemHeaderHeight = firstBlockRect.top - itemRect.top;

      // Measure each block's cumulative bottom position (relative to item)
      // This properly accounts for gaps/margins between blocks
      const blocks = contentBlocks.map((block, index) => {
        const blockRect = block.getBoundingClientRect();
        return {
          index,
          height: blockRect.height,
          // The bottom of this block relative to item start
          cumulativeBottom: blockRect.bottom - itemRect.top,
        };
      });

      return { itemHeaderHeight, blocks };
    };

    // Helper function to attempt content-level splitting of an item
    const tryContentSplit = (
      itemElement: HTMLElement,
      itemId: string,
      availableSpace: number,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _isFirstItemOnPage: boolean,
    ): { split: SplitItemRange; heightUsed: number; remainingStartIndex: number } | null => {
      const measurement = measureContentBlocks(itemElement);
      if (!measurement || measurement.blocks.length <= 1) {
        // Can't split single-block or unmeasurable items
        return null;
      }

      const { blocks } = measurement;
      const itemTotalHeight = itemElement.offsetHeight;

      // We need at least item header + first content block to fit
      // Use cumulativeBottom which properly accounts for gaps between blocks
      const firstBlockBottom = blocks[0].cumulativeBottom;
      if (availableSpace < firstBlockBottom) {
        return null;
      }

      // Find the last block that fits within available space
      let lastFittingIndex = -1;

      for (let blockIdx = 0; blockIdx < blocks.length; blockIdx++) {
        const block = blocks[blockIdx];
        // Check if this block's bottom fits in available space
        if (block.cumulativeBottom <= availableSpace) {
          lastFittingIndex = blockIdx;
        } else {
          break;
        }
      }

      // If all blocks fit, check if the full item (with any padding) also fits
      if (lastFittingIndex === blocks.length - 1) {
        // All content blocks fit, but the item might have extra padding at the end
        // Only return null (no split) if the full item actually fits
        if (itemTotalHeight <= availableSpace) {
          return null;
        }
        // Otherwise, we need to split to exclude the bottom padding
        // Return a split that includes all blocks but will use the actual height up to last block
      }

      // If no blocks fit after header, no split possible
      if (lastFittingIndex < 0) {
        return null;
      }

      const heightUsed = blocks[lastFittingIndex].cumulativeBottom;

      return {
        split: {
          itemId,
          startIndex: 0,
          endIndex: lastFittingIndex,
          hideItemHeader: false,
        },
        heightUsed,
        remainingStartIndex: lastFittingIndex + 1,
      };
    };

    while (i < items.length) {
      const item = items[i];
      const itemId = item.getAttribute("data-item-id");
      const itemRect = item.getBoundingClientRect();
      const itemHeight = itemRect.height;
      const itemStyle = window.getComputedStyle(item);
      const itemMarginTop = parseFloat(itemStyle.marginTop) || 0;

      // For items, we also consider margin if it's not the very first thing being placed in this specific flow?
      // Resume Studio usually has space-y-2 on list parents.
      // If we are placing the first item on a new page, does it retain margin?
      // Generally yes if inside a container, but if we are manually composing, we might drop it?
      // Safest: Use margin if (itemsOnCurrentPage.length > 0).

      const isFirstItem = itemsOnCurrentPage.length === 0 && splitItemsOnCurrentPage.length === 0;
      const itemSpaceNeeded = itemHeight + (isFirstItem ? 0 : itemMarginTop);

      if (!itemId) {
        i++;
        continue;
      }

      if (spaceRemaining >= itemSpaceNeeded) {
        itemsOnCurrentPage.push(itemId);
        currentSectionHeightOnPage += itemSpaceNeeded;
        spaceRemaining -= itemSpaceNeeded;
        i++;
      } else {
        // Item doesn't fit completely.
        const gapSize = spaceRemaining;

        // ORPHAN CHECK: If we have header but 0 items
        if (
          itemsOnCurrentPage.length === 0 &&
          splitItemsOnCurrentPage.length === 0 &&
          placingHeader
        ) {
          // First, try to content-split on CURRENT page before rolling back
          if (gapSize > MIN_GAP_FOR_CONTENT_SPLIT) {
            const splitResult = tryContentSplit(item, itemId, spaceRemaining, true);
            if (splitResult) {
              // We can fit some content on this page!
              itemsOnCurrentPage.push(itemId);
              splitItemsOnCurrentPage.push(splitResult.split);
              currentSectionHeightOnPage += splitResult.heightUsed;
              spaceRemaining -= splitResult.heightUsed;

              // Commit current page with partial item
              currentPageItems.push({
                id,
                type: "section",
                itemIds: [...itemsOnCurrentPage],
                hideHeader: !placingHeader,
                height: currentSectionHeightOnPage,
                top: currentHeight,
                splitItems: [...splitItemsOnCurrentPage],
              });
              currentHeight += currentSectionHeightOnPage;

              // Start new page for continuation
              startNewPage();
              currentHeight = 0;
              spaceRemaining = effectiveContentHeightPx;
              currentSectionHeightOnPage = 0;
              itemsOnCurrentPage.length = 0;
              splitItemsOnCurrentPage.length = 0;
              placingHeader = false;

              // Add continuation of split item
              const measurement = measureContentBlocks(item);
              if (measurement) {
                const itemTotalHeight = item.getBoundingClientRect().height;
                const lastSplitBlockBottom =
                  measurement.blocks[splitResult.remainingStartIndex - 1].cumulativeBottom;
                const remainingHeight = itemTotalHeight - lastSplitBlockBottom;

                itemsOnCurrentPage.push(itemId);
                splitItemsOnCurrentPage.push({
                  itemId,
                  startIndex: splitResult.remainingStartIndex,
                  endIndex: undefined,
                  hideItemHeader: true,
                });
                currentSectionHeightOnPage += remainingHeight;
                spaceRemaining -= remainingHeight;
              }
              i++;
              continue;
            }
          }

          // Content split didn't work or gap too small - rollback and move to next page
          if (currentHeight > 0 || (currentHeight === 0 && pages.length > 0)) {
            // (If we are at absolute 0 of page 0, we can't rollback further, just overflow)
            if (currentHeight > 0) {
              startNewPage();

              // Reset Logic on New Page
              currentHeight = 0;
              spaceRemaining = effectiveContentHeightPx;
              currentSectionHeightOnPage = 0;
              placingHeader = true;

              // Add Header
              currentSectionHeightOnPage += headerHeight;
              spaceRemaining -= headerHeight;

              // Retry Item - try content split if gap would be large
              const newGapSize = spaceRemaining;
              const newItemSpace = itemHeight;

              if (spaceRemaining >= newItemSpace) {
                itemsOnCurrentPage.push(itemId);
                currentSectionHeightOnPage += newItemSpace;
                spaceRemaining -= newItemSpace;
                i++;
              } else if (newGapSize > MIN_GAP_FOR_CONTENT_SPLIT) {
                // Try content-level split on fresh page
                const splitResult = tryContentSplit(item, itemId, spaceRemaining, true);
                if (splitResult) {
                  // IMPORTANT: Add the item to itemIds so the template renders it
                  itemsOnCurrentPage.push(itemId);
                  splitItemsOnCurrentPage.push(splitResult.split);
                  currentSectionHeightOnPage += splitResult.heightUsed;
                  spaceRemaining -= splitResult.heightUsed;

                  // Commit this page with partial item
                  currentPageItems.push({
                    id,
                    type: "section",
                    itemIds: [...itemsOnCurrentPage],
                    hideHeader: !placingHeader,
                    height: currentSectionHeightOnPage,
                    top: currentHeight,
                    splitItems: [...splitItemsOnCurrentPage],
                  });
                  currentHeight += currentSectionHeightOnPage;

                  // Start new page for continuation
                  startNewPage();
                  currentHeight = 0;
                  spaceRemaining = effectiveContentHeightPx;
                  currentSectionHeightOnPage = 0;
                  itemsOnCurrentPage.length = 0;
                  splitItemsOnCurrentPage.length = 0;
                  placingHeader = false;

                  // Add continuation of split item
                  const measurement = measureContentBlocks(item);
                  if (measurement) {
                    const itemTotalHeight = item.getBoundingClientRect().height;
                    const lastSplitBlockBottom =
                      measurement.blocks[splitResult.remainingStartIndex - 1].cumulativeBottom;
                    // Remaining height = total item height minus what we already placed
                    const remainingHeight = itemTotalHeight - lastSplitBlockBottom;

                    // IMPORTANT: Add the item to itemIds so the template renders it
                    itemsOnCurrentPage.push(itemId);
                    splitItemsOnCurrentPage.push({
                      itemId,
                      startIndex: splitResult.remainingStartIndex,
                      endIndex: undefined,
                      hideItemHeader: true,
                    });
                    currentSectionHeightOnPage += remainingHeight;
                    spaceRemaining -= remainingHeight;
                  }
                  i++;
                  continue;
                } else {
                  // Overflow - can't split
                  itemsOnCurrentPage.push(itemId);
                  currentSectionHeightOnPage += newItemSpace;
                  spaceRemaining -= newItemSpace;
                  i++;
                }
              } else {
                // Gap too small, just overflow
                itemsOnCurrentPage.push(itemId);
                currentSectionHeightOnPage += newItemSpace;
                spaceRemaining -= newItemSpace;
                i++;
              }
              continue;
            }
          }
        }

        // Try content-level splitting if gap is significant
        if (gapSize > MIN_GAP_FOR_CONTENT_SPLIT) {
          const splitResult = tryContentSplit(item, itemId, spaceRemaining, isFirstItem);

          if (splitResult) {
            // We can split this item!
            // IMPORTANT: Add the item to itemIds so the template renders it
            itemsOnCurrentPage.push(itemId);
            splitItemsOnCurrentPage.push(splitResult.split);
            currentSectionHeightOnPage += splitResult.heightUsed;
            spaceRemaining -= splitResult.heightUsed;

            // Commit current page with partial item
            currentPageItems.push({
              id,
              type: "section",
              itemIds: [...itemsOnCurrentPage],
              hideHeader: !placingHeader,
              height: currentSectionHeightOnPage,
              top: currentHeight,
              splitItems:
                splitItemsOnCurrentPage.length > 0 ? [...splitItemsOnCurrentPage] : undefined,
            });
            currentHeight += currentSectionHeightOnPage;

            // Start new page
            startNewPage();
            currentHeight = 0;
            spaceRemaining = effectiveContentHeightPx;
            currentSectionHeightOnPage = 0;
            itemsOnCurrentPage.length = 0;
            splitItemsOnCurrentPage.length = 0;
            placingHeader = false;

            // Add continuation of split item to new page
            const measurement = measureContentBlocks(item);
            if (measurement) {
              const itemTotalHeight = item.getBoundingClientRect().height;
              const lastSplitBlockBottom =
                measurement.blocks[splitResult.remainingStartIndex - 1].cumulativeBottom;
              // Remaining height = total item height minus what we already placed
              const remainingHeight = itemTotalHeight - lastSplitBlockBottom;

              // IMPORTANT: Add the item to itemIds so the template renders it
              itemsOnCurrentPage.push(itemId);
              splitItemsOnCurrentPage.push({
                itemId,
                startIndex: splitResult.remainingStartIndex,
                endIndex: undefined,
                hideItemHeader: true,
              });
              currentSectionHeightOnPage += remainingHeight;
              spaceRemaining -= remainingHeight;
            }

            i++;
            continue;
          }
        }

        // Gap too small or can't split - use existing behavior: move whole item to next page
        // Commit current chunk
        if (itemsOnCurrentPage.length > 0 || splitItemsOnCurrentPage.length > 0 || placingHeader) {
          currentPageItems.push({
            id,
            type: "section",
            itemIds: [...itemsOnCurrentPage],
            hideHeader: !placingHeader,
            height: currentSectionHeightOnPage,
            top: currentHeight,
            splitItems:
              splitItemsOnCurrentPage.length > 0 ? [...splitItemsOnCurrentPage] : undefined,
          });
          currentHeight += currentSectionHeightOnPage;
        }

        // Start New Page
        startNewPage();
        currentHeight = 0;
        spaceRemaining = effectiveContentHeightPx;
        currentSectionHeightOnPage = 0;
        itemsOnCurrentPage.length = 0;
        splitItemsOnCurrentPage.length = 0;
        placingHeader = false;

        // Retry Item on new page
        // First item on page: ignore margin
        const retrySpace = itemHeight;

        if (spaceRemaining >= retrySpace) {
          itemsOnCurrentPage.push(itemId);
          currentSectionHeightOnPage += retrySpace;
          spaceRemaining -= retrySpace;
          i++;
        } else {
          // Overflow (item larger than page)
          itemsOnCurrentPage.push(itemId);
          currentSectionHeightOnPage += retrySpace;
          spaceRemaining -= retrySpace;
          i++;
        }
      }
    }

    // Push final chunk of the section
    if (itemsOnCurrentPage.length > 0 || splitItemsOnCurrentPage.length > 0 || placingHeader) {
      currentPageItems.push({
        id,
        type: "section",
        itemIds: [...itemsOnCurrentPage],
        hideHeader: !placingHeader,
        height: currentSectionHeightOnPage,
        top: currentHeight,
        splitItems: splitItemsOnCurrentPage.length > 0 ? [...splitItemsOnCurrentPage] : undefined,
      });
      currentHeight += currentSectionHeightOnPage;
    }
  }

  // Push final page
  if (currentPageItems.length > 0) {
    pages.push({
      pageIndex,
      items: currentPageItems,
      height: currentHeight,
    });
  }

  return pages;
};

/**
 * Calculate layout for multi-column templates (e.g., Arcanine).
 * Each column is processed independently, but page breaks occur when
 * ANY column fills up. Empty columns are preserved on subsequent pages.
 *
 * @param columnContainers - Array of HTMLElements, one per column
 * @param format - Page format (a4, letter, etc.)
 * @param pageMargin - Top/bottom margins
 * @param headerHeight - Height of the header section (only on first page)
 * @param containerPadding - Vertical padding from the template container (p-custom)
 */
export const calculateMultiColumnLayout = (
  columnContainers: HTMLElement[],
  format: PageFormat = "a4",
  pageMargin: { top: number; bottom: number } = { top: 0, bottom: 0 },
  headerHeight: number = 0,
  containerPadding: { top: number; bottom: number } = { top: 0, bottom: 0 },
): MultiColumnPageLayout[] => {
  const pageHeightPx = pageSizeMap[format].height * MM_TO_PX;
  // Apply bottom threshold to prevent content from going too close to page edge
  const contentHeightPx =
    pageHeightPx - (pageMargin.top + pageMargin.bottom) - PAGE_BOTTOM_THRESHOLD_PX;

  // Use provided container padding (from template's p-custom class)
  // This accounts for the vertical padding on the template container
  const paddingTop = containerPadding.top;
  const paddingBottom = containerPadding.bottom;

  // Effective content height = A4 - margins - padding - safety margin
  // The safety margin prevents content from overflowing pages due to
  // sub-pixel rendering differences between browser and Puppeteer
  const effectiveContentHeightPx =
    contentHeightPx - (paddingTop + paddingBottom) - LAYOUT_SAFETY_MARGIN_PX;

  // Track state for each column
  interface ColumnState {
    columnIndex: number;
    children: HTMLElement[];
    childIndex: number;
    exhausted: boolean;
  }

  const columnStates: ColumnState[] = columnContainers.map((container, idx) => ({
    columnIndex: idx,
    children: Array.from(container.children).filter(
      (child) => child instanceof HTMLElement && (child as HTMLElement).style.display !== "none",
    ) as HTMLElement[],
    childIndex: 0,
    exhausted: false,
  }));

  const pages: MultiColumnPageLayout[] = [];
  let pageIndex = 0;
  let isFirstPage = true;

  // Process pages until all columns are exhausted
  while (columnStates.some((c) => !c.exhausted)) {
    // Calculate available height for this page (subtract header on first page)
    const availableHeight = isFirstPage
      ? effectiveContentHeightPx - headerHeight
      : effectiveContentHeightPx;

    const pageColumns: ColumnLayout[] = [];

    // Process each column for this page
    for (const state of columnStates) {
      let columnHeight = 0;
      const columnItems: LayoutItem[] = [];

      if (state.exhausted) {
        // Column already finished, add empty column layout
        pageColumns.push({
          columnIndex: state.columnIndex,
          items: [],
          height: 0,
        });
        continue;
      }

      // Fill this column until it hits the page boundary
      while (state.childIndex < state.children.length) {
        const child = state.children[state.childIndex];
        const id = child.getAttribute("data-id") || child.id;

        if (!id) {
          state.childIndex++;
          continue;
        }

        // Check if this is a continuation from previous page
        const continuationIndex = (child as any).__continuationIndex as number | undefined;
        const continuationSplitStart = (child as any).__continuationSplitStart as
          | SplitItemRange
          | undefined;
        // Continuation if: we have a continuation index OR we have a content-split to continue
        // Note: continuationIndex can be 0 if the first item was content-split
        const isContinuation =
          continuationIndex !== undefined ||
          (continuationSplitStart !== undefined && continuationSplitStart !== null);

        const childRect = child.getBoundingClientRect();
        const childHeight = childRect.height;
        const style = window.getComputedStyle(child);
        const marginTop = parseFloat(style.marginTop) || 0;

        // Effective margin: ignore for first item on page
        const effectiveMarginTop = columnItems.length === 0 ? 0 : marginTop;

        // For continuation, we don't use full section height
        if (isContinuation) {
          // This section was partially rendered - continue from where we left off
          const contentWrapper = child.querySelector(
            '[data-role="section-content"]',
          ) as HTMLElement;

          if (!contentWrapper) {
            // Shouldn't happen but fallback
            state.childIndex++;
            delete (child as any).__continuationIndex;
            delete (child as any).__continuationSplitStart;
            continue;
          }

          const items = Array.from(contentWrapper.children) as HTMLElement[];
          let spaceRemaining = availableHeight - columnHeight - effectiveMarginTop;
          let sectionHeightOnPage = 0;
          const itemsOnPage: string[] = [];
          const splitItemsOnPage: SplitItemRange[] = [];

          // Handle continuation of content-split item
          // If continuationIndex is undefined but we have a split start, use 0
          let itemIndex = continuationIndex ?? 0;

          // If the previous page split an item mid-content, continue that item first
          if (continuationSplitStart) {
            const prevItemId = continuationSplitStart.itemId;
            const prevEndIndex = continuationSplitStart.endIndex ?? 0;
            const item = items.find((i) => i.getAttribute("data-item-id") === prevItemId);

            if (item) {
              // Add continuation of the split item
              itemsOnPage.push(prevItemId);
              splitItemsOnPage.push({
                itemId: prevItemId,
                startIndex: prevEndIndex + 1, // Continue from where we left off
                hideItemHeader: true, // Hide header for continuation
              });

              // Estimate remaining height for this item
              const contentBlocks = Array.from(
                item.querySelectorAll("[data-content-block]"),
              ) as HTMLElement[];
              if (contentBlocks.length > prevEndIndex + 1) {
                const lastBlock = contentBlocks[contentBlocks.length - 1];
                const firstContinuationBlock = contentBlocks[prevEndIndex + 1];
                const continuationHeight =
                  lastBlock.getBoundingClientRect().bottom -
                  firstContinuationBlock.getBoundingClientRect().top;
                sectionHeightOnPage += continuationHeight;
                spaceRemaining -= continuationHeight;
              }

              itemIndex++; // Move past this item
            }
          }

          // Continue with remaining items
          while (itemIndex < items.length && spaceRemaining > 0) {
            const item = items[itemIndex];
            const itemId = item.getAttribute("data-item-id");

            if (!itemId) {
              itemIndex++;
              continue;
            }

            const itemBoundingRect = item.getBoundingClientRect();
            const itemHeight = itemBoundingRect.height;
            const itemStyle = window.getComputedStyle(item);
            const itemMarginTop = parseFloat(itemStyle.marginTop) || 0;
            const isFirstItem = itemsOnPage.length === 0;
            const itemSpaceNeeded = itemHeight + (isFirstItem ? 0 : itemMarginTop);

            if (spaceRemaining >= itemSpaceNeeded) {
              // Item fits
              itemsOnPage.push(itemId);
              sectionHeightOnPage += itemSpaceNeeded;
              spaceRemaining -= itemSpaceNeeded;
              itemIndex++;
            } else {
              // Item doesn't fit - try content-level split if gap is significant
              if (spaceRemaining > MIN_GAP_FOR_CONTENT_SPLIT) {
                const splitResult = tryContentSplitForMultiColumn(item, itemId, spaceRemaining);
                if (splitResult) {
                  itemsOnPage.push(itemId);
                  splitItemsOnPage.push(splitResult.split);
                  sectionHeightOnPage += splitResult.heightUsed;
                }
              }
              break;
            }
          }

          // Add the continuation section if we have items
          if (itemsOnPage.length > 0) {
            columnItems.push({
              id,
              type: "section",
              itemIds: itemsOnPage,
              hideHeader: true, // Hide section header for continuation
              height: sectionHeightOnPage,
              top: columnHeight + effectiveMarginTop,
              splitItems: splitItemsOnPage.length > 0 ? splitItemsOnPage : undefined,
            });
            columnHeight += effectiveMarginTop + sectionHeightOnPage;
          }

          // Check if section is complete
          if (itemIndex >= items.length) {
            // Section complete, move to next
            state.childIndex++;
            delete (child as any).__continuationIndex;
            delete (child as any).__continuationSplitStart;
          } else {
            // Still more to render - update continuation info
            (child as any).__continuationIndex = itemIndex;
            (child as any).__continuationSplitStart =
              splitItemsOnPage.length > 0 ? splitItemsOnPage[splitItemsOnPage.length - 1] : null;
            break; // Move to next page
          }
          continue;
        }

        // Non-continuation: check if section fits completely
        const spaceNeeded = childHeight + effectiveMarginTop;

        // Check if this section fits
        if (columnHeight + spaceNeeded <= availableHeight) {
          // It fits completely
          columnItems.push({
            id,
            type: "section",
            height: childHeight,
            top: columnHeight + effectiveMarginTop,
          });
          columnHeight += spaceNeeded;
          state.childIndex++;
        } else {
          // Doesn't fit - try to split the section
          const contentWrapper = child.querySelector(
            '[data-role="section-content"]',
          ) as HTMLElement;

          if (!contentWrapper) {
            // Can't split, will handle on next page
            break;
          }

          // Try to split the section
          const contentWrapperRect = contentWrapper.getBoundingClientRect();
          const sectionHeaderHeight = contentWrapperRect.top - childRect.top;
          const items = Array.from(contentWrapper.children) as HTMLElement[];

          // Check if at least the header fits
          if (columnHeight + effectiveMarginTop + sectionHeaderHeight > availableHeight) {
            // Header doesn't fit, move entire section to next page
            break;
          }

          // Header fits, try to fit items
          let spaceRemaining =
            availableHeight - columnHeight - effectiveMarginTop - sectionHeaderHeight;
          let sectionHeightOnPage = sectionHeaderHeight;
          const itemsOnPage: string[] = [];
          const splitItemsOnPage: SplitItemRange[] = [];
          let itemIndex = 0;

          while (itemIndex < items.length) {
            const item = items[itemIndex];
            const itemId = item.getAttribute("data-item-id");

            if (!itemId) {
              itemIndex++;
              continue;
            }

            const itemRect = item.getBoundingClientRect();
            const itemHeight = itemRect.height;
            const itemStyle = window.getComputedStyle(item);
            const itemMarginTop = parseFloat(itemStyle.marginTop) || 0;
            const isFirstItem = itemsOnPage.length === 0;
            const itemSpaceNeeded = itemHeight + (isFirstItem ? 0 : itemMarginTop);

            if (spaceRemaining >= itemSpaceNeeded) {
              // Item fits
              itemsOnPage.push(itemId);
              sectionHeightOnPage += itemSpaceNeeded;
              spaceRemaining -= itemSpaceNeeded;
              itemIndex++;
            } else {
              // Item doesn't fit - try content-level split if gap is significant
              const gapSize = spaceRemaining;

              if (gapSize > MIN_GAP_FOR_CONTENT_SPLIT) {
                const splitResult = tryContentSplitForMultiColumn(item, itemId, spaceRemaining);
                if (splitResult) {
                  itemsOnPage.push(itemId);
                  splitItemsOnPage.push(splitResult.split);
                  sectionHeightOnPage += splitResult.heightUsed;
                  // Mark that this section needs continuation
                  // We don't increment itemIndex here - continuation handled separately
                }
              }
              break;
            }
          }

          // If we placed any items, add the partial section
          if (itemsOnPage.length > 0) {
            columnItems.push({
              id,
              type: "section",
              itemIds: itemsOnPage,
              hideHeader: false,
              height: sectionHeightOnPage,
              top: columnHeight + effectiveMarginTop,
              splitItems: splitItemsOnPage.length > 0 ? splitItemsOnPage : undefined,
            });
            columnHeight += effectiveMarginTop + sectionHeightOnPage;

            // If we didn't finish the section, don't increment childIndex
            if (itemIndex < items.length) {
              // Section needs continuation - mark where we left off
              // Store continuation info for next page
              (child as any).__continuationIndex = itemIndex;
              (child as any).__continuationSplitStart =
                splitItemsOnPage.length > 0 ? splitItemsOnPage[splitItemsOnPage.length - 1] : null;
            } else {
              state.childIndex++;
            }
          }
          break;
        }
      }

      // Check if column is exhausted
      if (state.childIndex >= state.children.length) {
        state.exhausted = true;
      }

      pageColumns.push({
        columnIndex: state.columnIndex,
        items: columnItems,
        height: columnHeight,
      });
    }

    // Determine page height (max of all columns)
    const pageHeight = Math.max(...pageColumns.map((c) => c.height), 0);

    pages.push({
      pageIndex,
      columns: pageColumns,
      pageHeight,
    });

    pageIndex++;
    isFirstPage = false;
  }

  return pages;
};

/**
 * Helper for content-level splitting in multi-column context
 */
const tryContentSplitForMultiColumn = (
  itemElement: HTMLElement,
  itemId: string,
  availableSpace: number,
): { split: SplitItemRange; heightUsed: number; remainingStartIndex: number } | null => {
  const contentBlocks = Array.from(
    itemElement.querySelectorAll("[data-content-block]"),
  ) as HTMLElement[];

  if (contentBlocks.length <= 1) return null;

  const itemRect = itemElement.getBoundingClientRect();

  // Measure blocks
  const blocks = contentBlocks.map((block, index) => {
    const blockRect = block.getBoundingClientRect();
    return {
      index,
      cumulativeBottom: blockRect.bottom - itemRect.top,
    };
  });

  // Need at least header + first block
  if (availableSpace < blocks[0].cumulativeBottom) return null;

  // Find last fitting block
  let lastFittingIndex = -1;
  for (let i = 0; i < blocks.length; i++) {
    if (blocks[i].cumulativeBottom <= availableSpace) {
      lastFittingIndex = i;
    } else {
      break;
    }
  }

  if (lastFittingIndex < 0) return null;

  return {
    split: {
      itemId,
      startIndex: 0,
      endIndex: lastFittingIndex,
      hideItemHeader: false,
    },
    heightUsed: blocks[lastFittingIndex].cumulativeBottom,
    remainingStartIndex: lastFittingIndex + 1,
  };
};

// ============================================================================
// COVER LETTER PAGINATION
// ============================================================================

/**
 * Content split range for cover letter letterBody field.
 * Tracks which content blocks (paragraphs, list items) to render on each page.
 */
export interface CoverContentSplitRange {
  startIndex?: number; // Starting content block index (0 = from beginning)
  endIndex?: number; // Ending content block index (undefined = to end)
}

/**
 * Page layout for cover letters.
 * Cover letters have a simpler structure: header (fixed fields) + letterBody (rich text).
 */
export interface CoverPageLayout {
  pageIndex: number;
  showHeader: boolean; // Only true for first page
  splitRange?: CoverContentSplitRange; // Which content blocks to render
  height: number;
}

/**
 * Calculate layout for cover letter templates.
 * Cover letters are simpler than resumes - they have a fixed header section
 * and a single rich text letterBody that can be split across pages.
 *
 * The algorithm:
 * 1. Measure the header section (everything marked with data-cover-header)
 * 2. Measure the letterBody content blocks (marked with data-content-block by RichTextContent)
 * 3. Page 1: header + as much letterBody as fits
 * 4. Subsequent pages: continued letterBody content with splitRange
 *
 * @param container - The template container element
 * @param format - Page format (a4, letter, etc.)
 */
export const calculateCoverLayout = (
  container: HTMLElement,
  format: PageFormat = "a4",
): CoverPageLayout[] => {
  const pageHeightPx = pageSizeMap[format].height * MM_TO_PX;
  const contentHeightPx = pageHeightPx - PAGE_BOTTOM_THRESHOLD_PX;

  // Measure container padding
  const containerStyle = window.getComputedStyle(container);
  const paddingTop = parseFloat(containerStyle.paddingTop) || 0;
  const paddingBottom = parseFloat(containerStyle.paddingBottom) || 0;

  const effectiveContentHeightPx =
    contentHeightPx - (paddingTop + paddingBottom) - LAYOUT_SAFETY_MARGIN_PX;

  const pages: CoverPageLayout[] = [];

  // Find header section (marked with data-cover-header)
  const headerElement = container.querySelector("[data-cover-header]") as HTMLElement | null;
  const headerHeight = headerElement?.getBoundingClientRect().height ?? 0;

  // Find letterBody content (marked with data-cover-content)
  const contentElement = container.querySelector("[data-cover-content]") as HTMLElement | null;

  if (!contentElement) {
    // No content to paginate - just render single page
    pages.push({
      pageIndex: 0,
      showHeader: true,
      height: headerHeight,
    });
    return pages;
  }

  // Find all content blocks within the letterBody
  const contentBlocks = Array.from(
    contentElement.querySelectorAll("[data-content-block]"),
  ) as HTMLElement[];

  if (contentBlocks.length === 0) {
    // No splittable content blocks - render as single page
    const totalHeight = headerHeight + contentElement.getBoundingClientRect().height;
    pages.push({
      pageIndex: 0,
      showHeader: true,
      height: Math.min(totalHeight, effectiveContentHeightPx),
    });
    return pages;
  }

  // Measure content element position relative to container for gap calculation
  const containerRect = container.getBoundingClientRect();
  const contentRect = contentElement.getBoundingClientRect();
  const contentOffsetFromHeader = contentRect.top - containerRect.top - paddingTop;

  // Measure each block's position relative to content element
  const blocks = contentBlocks.map((block, index) => {
    const blockRect = block.getBoundingClientRect();
    return {
      index,
      height: blockRect.height,
      top: blockRect.top - contentRect.top,
      bottom: blockRect.bottom - contentRect.top,
    };
  });

  // Page 1: header + as much content as fits
  let pageIndex = 0;
  let currentBlockIndex = 0;
  let isFirstPage = true;

  while (currentBlockIndex < blocks.length) {
    const availableHeight = isFirstPage
      ? effectiveContentHeightPx - contentOffsetFromHeader
      : effectiveContentHeightPx;

    let heightUsed = 0;
    const startIndex = currentBlockIndex;
    let endIndex = currentBlockIndex;

    // Find how many blocks fit on this page
    while (currentBlockIndex < blocks.length) {
      const block = blocks[currentBlockIndex];
      // Calculate block height from its position in the content
      const blockBottom = block.bottom;

      // For first page, we start from the content offset
      // For subsequent pages, blocks are positioned from 0
      const effectiveBlockBottom = isFirstPage ? blockBottom : blockBottom - blocks[startIndex].top;

      if (effectiveBlockBottom <= availableHeight) {
        heightUsed = effectiveBlockBottom;
        endIndex = currentBlockIndex;
        currentBlockIndex++;
      } else {
        break;
      }
    }

    // If no blocks fit, force at least one block (overflow case)
    if (endIndex < startIndex && startIndex < blocks.length) {
      endIndex = startIndex;
      heightUsed = blocks[startIndex].height;
      currentBlockIndex = startIndex + 1;
    }

    pages.push({
      pageIndex,
      showHeader: isFirstPage,
      // Only specify splitRange if we're not showing all content
      splitRange:
        startIndex > 0 || endIndex < blocks.length - 1 ? { startIndex, endIndex } : undefined,
      height: isFirstPage ? contentOffsetFromHeader + heightUsed : heightUsed,
    });

    pageIndex++;
    isFirstPage = false;
  }

  // Handle case where header fills the page but no content blocks fit
  if (pages.length === 0) {
    pages.push({
      pageIndex: 0,
      showHeader: true,
      height: headerHeight,
    });
  }

  return pages;
};
